import { formatOffset, parseOffset, } from "./offset.js";
const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const londonParts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/London",
    year: "numeric",
    month: "2-digit",
});
const baseMonth = () => {
    const parts = {};
    for (const { type, value } of londonParts.formatToParts(new Date())) {
        parts[type] = value;
    }
    return { month0: Number(parts.month) - 1, year: Number(parts.year) % 100 };
};
const yy = (year) => String(year).slice(-2).padStart(2, "0");
/**
 * The canonical label of the base month: the current month in London, the anchor
 * every relative offset resolves against (`M+0` is the base month itself).
 *
 * Period rows are anchored to "now in London", not the user's local wall-clock,
 * so users in other timezones never see periods roll early or late near month
 * boundaries. Recomputed on every call.
 *
 * @example
 * ```ts
 * artis.v1.offsets.getBaseMonth(); // "Aug26"
 * ```
 */
export function getBaseMonth() {
    const { month0, year } = baseMonth();
    return `${MONTH_LABELS[month0]}${yy(year)}`;
}
const relativeToLabel = ({ month0, year }, family, index) => {
    switch (family) {
        case "M": {
            const total = month0 + index;
            return `${MONTH_LABELS[total % 12]}${yy(year + Math.floor(total / 12))}`;
        }
        case "Q": {
            const monthAt = month0 + index * 3;
            return `Q${(Math.floor(monthAt / 3) % 4) + 1}${yy(year + Math.floor(monthAt / 12))}`;
        }
        case "H": {
            const halfAt = Math.floor(month0 / 6) + index;
            return `H${(halfAt % 2) + 1}${yy(year + Math.floor(halfAt / 2))}`;
        }
        case "S":
            return `${index % 2 === 0 ? "Sum" : "Win"}${yy(year + Math.floor(index / 2))}`;
        case "C":
            return `Cal${yy(year + index)}`;
    }
};
const RELATIVE_FAMILY = {
    month: "M",
    quarter: "Q",
    half: "H",
    season: "S",
    calendar: "C",
};
const absoluteToIndex = ({ month0, year }, period) => {
    switch (period.kind) {
        case "month":
            return (period.year - year) * 12 + (period.month - 1 - month0);
        case "quarter":
            return (period.year - year) * 4 + (period.index - 1 - Math.floor(month0 / 3));
        case "half":
            return (period.year - year) * 2 + (period.index - 1 - Math.floor(month0 / 6));
        case "season":
            return (period.year - year) * 2 + (period.season === "Sum" ? 0 : 1);
        case "calendar":
            return period.year - year;
    }
};
/**
 * Rewrite an {@link Offset} as the absolute period it points at right now: `"M+5"`
 * becomes `"Jan27"` when the base month is `"Aug26"`. An absolute offset passes
 * through as its canonical spelling; an invalid one returns the grammar error.
 *
 * The anchoring matches the grid's relative rows: `M`, `Q` and `H` count from the
 * period holding the base month, `S` counts `Sum`/`Win` pairs from the current year
 * regardless of month, and `C` counts years.
 *
 * @example
 * ```ts
 * artis.v1.offsets.toAbsolute("M+5");   // { normalized: "Jan27" }
 * artis.v1.offsets.toAbsolute("mar26"); // { normalized: "Mar26" }
 * ```
 */
export function toAbsoluteOffset(offset) {
    const { parsed, error } = parseOffset(offset);
    if (!parsed)
        return { error };
    if (parsed.kind === "absolute")
        return { normalized: parsed.period.label };
    return { normalized: relativeToLabel(baseMonth(), parsed.family, parsed.index) };
}
/**
 * Rewrite an {@link Offset} as the relative offset that points at it right now:
 * `"Jan27"` becomes `"M+5"` when the base month is `"Aug26"`. A relative offset
 * passes through as its canonical spelling; an invalid one returns the grammar error.
 *
 * A period before the current one has no relative spelling (the grammar has no
 * negative offsets), so it comes back as an `error`.
 *
 * @example
 * ```ts
 * artis.v1.offsets.toRelative("Jan27"); // { normalized: "M+5" }
 * artis.v1.offsets.toRelative("Feb26"); // { error: 'cannot convert "Feb26" …' }
 * ```
 */
export function toRelativeOffset(offset) {
    const { parsed, error } = parseOffset(offset);
    if (!parsed)
        return { error };
    if (parsed.kind === "relative")
        return { normalized: formatOffset(parsed) };
    const index = absoluteToIndex(baseMonth(), parsed.period);
    if (index < 0) {
        return { error: `cannot convert "${parsed.period.label}" to a relative offset: it is before the current period` };
    }
    return { normalized: `${RELATIVE_FAMILY[parsed.period.kind]}+${index}` };
}
//# sourceMappingURL=convert.js.map